import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Flex,
    Icon,
    Spinner,
    Stack,
    Table,
    TabPanel,
    TabPanels,
    Tabs,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { IoCloseCircleSharp } from 'react-icons/io5';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import { MdOutlineCloudUpload } from 'react-icons/md';
import { useMemberGroup } from 'contexts/MemberGroupContext';
import CButton from 'components/CButton';
import Dropzone from 'components/Dropzone'
import MemberGroupDropdown from 'components/MemberGroupDropdown';

export default function ImportGroupMembers() {
    const { importGroupMembers, importGroupMemberSave } = useMemberGroup();

    const textColor = useColorModeValue('secondaryGray.900', 'white');

    const [data, setData] = useState([]);
    const [validated, setValidated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [groupDetail, setGroupDetail] = useState({
        group_id: null,
        members_csv: null
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidated(true);

        if (!groupDetail.group_id || !groupDetail.members_csv) return;

        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('group_id', groupDetail?.group_id?.value ? groupDetail.group_id.value : null);
            formData.append('members_csv', groupDetail.members_csv);

            const data = await importGroupMembers(formData);
            if (!data.error) {
                setData(data.data)
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => {
        const baseColumns = [
            columnHelper.accessor('first_name', {
                header: () => <Text color="gray.400">FIRST NAME</Text>,
                cell: (info) => (
                    <Text color={textColor} fontSize="md" fontWeight="500">
                        {info.getValue()}
                    </Text>
                ),
            }),
            columnHelper.accessor('last_name', {
                header: () => <Text color="gray.400">LAST NAME</Text>,
                cell: (info) => (
                    <Text color={textColor} fontSize="md" fontWeight="500">
                        {info.getValue()}
                    </Text>
                ),
            }),
            columnHelper.accessor('email', {
                header: () => <Text color="gray.400">EMAIL</Text>,
                cell: (info) => (
                    <Text color={textColor} fontSize="md" fontWeight="500">
                        {info.getValue()}
                    </Text>
                ),
            }),
            columnHelper.accessor('has_error', {
                header: () => <Text color="gray.400">IS VALID</Text>,
                cell: (info) => (
                    <>
                        {
                            info.getValue() === true ? (
                                <Icon
                                    as={IoCloseCircleSharp}
                                    h={6}
                                    w={6}
                                    mt='10px'
                                    me='20px'
                                    cursor='pointer'
                                    title="Edit"
                                    color='red'
                                />
                            ) : (
                                <Icon
                                    as={IoIosCheckmarkCircle}
                                    h={6}
                                    w={6}
                                    mt='10px'
                                    me='20px'
                                    cursor='pointer'
                                    title="Delete"
                                    color='green'
                                />
                            )
                        }
                    </>
                ),
            }),
            columnHelper.accessor('error_message', {
                header: () => <Text color="gray.400">ISSUE</Text>,
                cell: (info) => (
                    <Text color='red' fontSize="md" fontWeight="500" className='ellipsis-wrap' width='300px'>
                        {info.getValue()}
                    </Text>
                ),
            }),
        ]

        return baseColumns;
    }, []);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handleReset = () => {
        setData([]);
        setGroupDetail({
            group_id: null,
            members_csv: null
        });
        setValidated(false)
    }

    const handleSaveGroupMemberImport = async () => {
        setIsLoading(true)
        try {
            const importedData = {
                group_id: groupDetail?.group_id?.value ? groupDetail.group_id.value : null,
                members: data
            }
            await importGroupMemberSave(importedData)
            handleReset()
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Flex direction="column" minH="100vh" align="start" top="0" pt={{ sm: '125px', lg: '75px' }} position="relative" w="100%">
            <Tabs w="100%">
                <TabPanels mt="24px" maxW="100%" w="100%" mx="auto">
                    <TabPanel w="100%" p="0px" mx="auto">
                        <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
                            Import Group Members Info
                        </Text>
                        {isLoading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
                                <Spinner color="#fa8f03" />
                            </Box>
                        ) : (
                            <>
                                <form onSubmit={handleSubmit}>
                                    <Flex direction="row" w="100%" justify="space-between">
                                        <Stack direction="column" spacing="20px" w={{ base: "100%", md: "48%" }}>
                                            <MemberGroupDropdown
                                                value={groupDetail.group_id}
                                                onChange={(value) => setGroupDetail({ ...groupDetail, group_id: value })}
                                                required={true}
                                                multiSelect={false}
                                                error={!groupDetail.group_id && validated}
                                            />
                                            <Dropzone
                                                content={
                                                    <Box>
                                                        <Icon
                                                            as={MdOutlineCloudUpload}
                                                            w="80px"
                                                            h="80px"
                                                            color='#fa8f03'
                                                        />
                                                        <Text
                                                            mx="auto"
                                                            mb="12px"
                                                            fontSize="lg"
                                                            fontWeight="700"
                                                            whiteSpace="pre-wrap"
                                                            color={textColor}
                                                        >
                                                            Drop your files here, or browse
                                                        </Text>
                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="500"
                                                            color="secondaryGray.500"
                                                        >
                                                            Only
                                                            <Text
                                                                as="span"
                                                                fontSize="sm"
                                                                fontWeight="500"
                                                                color='#fa8f03'
                                                            >
                                                                {' '}csv{' '}
                                                            </Text>
                                                            files are allowed
                                                        </Text>
                                                    </Box>
                                                }
                                                value={groupDetail.members_csv}
                                                onChange={(file) => setGroupDetail({ ...groupDetail, members_csv: file })}
                                                required={true}
                                                error={!groupDetail.members_csv && validated}
                                            />
                                        </Stack>
                                    </Flex>
                                    <Flex justify="flex-end" align="center" mt="44px" gap="3">
                                        <CButton
                                            w={{ base: '128px', md: '148px' }}
                                            h="46px"
                                            type="submit">
                                            Upload
                                        </CButton>
                                    </Flex>
                                </form>

                                {
                                    data.length > 0 && (
                                        <>
                                            <Box border="2px solid #fa8f03" borderRadius="10px" overflow="auto" maxHeight="520px" marginTop='15px'>
                                                <Table variant="simple">
                                                    <Thead>
                                                        {table.getHeaderGroups().map((headerGroup) => (
                                                            <Tr key={headerGroup.id} bg='#fa8f03'>
                                                                {headerGroup.headers.map((header) => (
                                                                    <Th key={header.id} borderColor='#fa8f03'>
                                                                        {header.isPlaceholder ? null : (
                                                                            <Flex
                                                                                onClick={header.column.getToggleSortingHandler()}
                                                                                cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                                                                justify="space-between"
                                                                                align="center"
                                                                                color="white"
                                                                            >
                                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                                            </Flex>
                                                                        )}
                                                                    </Th>
                                                                ))}
                                                            </Tr>
                                                        ))}
                                                    </Thead>
                                                    <Tbody>
                                                        {data.length > 0 ? (
                                                            table.getRowModel().rows.map((row) => (
                                                                <Tr key={row.id}>
                                                                    {row.getVisibleCells().map((cell) => (
                                                                        <Td key={cell.id} fontSize="sm" borderColor='#fa8f03'>
                                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                        </Td>
                                                                    ))}
                                                                </Tr>
                                                            ))
                                                        ) : (
                                                            <Tr>
                                                                <Td colSpan={columns.length} textAlign="center">
                                                                    No members found.
                                                                </Td>
                                                            </Tr>
                                                        )}
                                                    </Tbody>
                                                </Table>
                                            </Box>
                                            <Flex justify="flex-end" align="center" mt="44px" gap="3">
                                                <Button
                                                    variant="darkBrand"
                                                    fontSize="sm"
                                                    borderRadius="10px"
                                                    w={{ base: '128px', md: '148px' }}
                                                    h="46px" type="button"
                                                    bg="gray"
                                                    color="white"
                                                    _hover={{ bg: 'gray.600' }}
                                                    onClick={handleReset}>
                                                    Reset
                                                </Button>
                                                <CButton
                                                    type="submit"
                                                    w={{ base: '128px', md: '148px' }}
                                                    h="46px"
                                                    onClick={handleSaveGroupMemberImport}>
                                                    Submit
                                                </CButton>
                                            </Flex>
                                        </>
                                    )
                                }
                            </>
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Flex>
    );
}
